import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Database,
  MessageSquare,
  ArrowRight,
  BarChart3,
  Zap,
  TrendingUp,
  Activity,
  Timer,
  Target,
  ChevronDown,
  ChevronUp,
  Download,
  Search,
  Filter,
  Eye,
  Copy,
  ExternalLink,
  RefreshCw,
  Cpu,
  MessageCircle,
  Star,
  TrendingDown,
  Grid,
  List,
  GitBranch
} from 'lucide-react';
import { getFlowExecution, getFlow, getProject } from '../services/api';

const FlowResults = () => {
  const { projectId, flowId, executionId } = useParams();
  const navigate = useNavigate();
  
  const [execution, setExecution] = useState(null);
  const [flow, setFlow] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  
  // Analytics state
  const [analyticsData, setAnalyticsData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedScoreFilter, setSelectedScoreFilter] = useState('all');
  const [selectedModelFilter, setSelectedModelFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    loadData();
  }, [executionId]);

  useEffect(() => {
    let pollInterval = null;
    
    // Start polling if execution is still running
    if (execution && (execution.status === 'running' || execution.status === 'pending')) {
      pollInterval = startPolling();
    }
    
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      setPolling(false);
    };
  }, [execution]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [executionData, flowData, projectData] = await Promise.all([
        getFlowExecution(flowId, executionId),
        getFlow(flowId),
        getProject(projectId)
      ]);
      
      setExecution(executionData);
      setFlow(flowData);
      setProject(projectData);
      
      // Process analytics data
      processAnalyticsData(executionData);
    } catch (error) {
      console.error('Error loading flow results:', error);
    } finally {
      setLoading(false);
    }
  };

  const processAnalyticsData = (executionData) => {
    if (!executionData?.nodeResults) return;
    
    const analytics = [];
    const promptResults = {};
    const evaluationResults = {};
    const testSetData = {};
    
    // First pass: collect data by node type
    executionData.nodeResults.forEach(nodeResult => {
      try {
        const parsedResult = JSON.parse(nodeResult.result);
        
        if (parsedResult.type === 'testSetNode') {
          testSetData[nodeResult.node_id] = parsedResult;
        } else if (parsedResult.type === 'promptNode' && parsedResult.output) {
          promptResults[nodeResult.node_id] = {
            ...parsedResult,
            nodeId: nodeResult.node_id
          };
        } else if (parsedResult.type === 'autoEvaluatorNode' && parsedResult.output) {
          evaluationResults[nodeResult.node_id] = {
            ...parsedResult,
            nodeId: nodeResult.node_id
          };
        } else if (parsedResult.type === 'splitNode') {
          // Split nodes don't generate analytics data directly, but we can track their decisions
        }
      } catch (e) {
        console.error('Error parsing node result:', e);
      }
    });
    
    
    Object.values(promptResults).forEach(promptResult => {
      if (promptResult.output?.results) {
        promptResult.output.results.forEach((result, index) => {
          const baseAnalytics = {
            id: `${promptResult.nodeId}-${index}`,
            promptName: promptResult.promptName || promptResult.prompt || promptResult.name || promptResult.nodeId,
            promptVersion: promptResult.version || '1.0',
            model: result.model || promptResult.model || 'Unknown',
            input: result.input || 'N/A',
            response: result.response || result.output || 'N/A',
            tokenCount: result.tokenCount || result.tokens || 0,
            executionTime: result.executionTime || 0,
            timestamp: result.timestamp || new Date().toISOString(),
            status: result.status || 'completed',
            promptNodeId: promptResult.nodeId,
            testCaseIndex: index
          };
          
          // Find corresponding evaluation scores - improved matching logic
          let foundEvaluation = false;
          
          Object.values(evaluationResults).forEach(evalResult => {
            if (evalResult.output?.results) {
              evalResult.output.results.forEach(evalItem => {
                // Try multiple matching strategies
                const matchesResponse = evalItem.response === result.response;
                const matchesIndex = evalItem.testCaseIndex === index;
                const matchesTestCaseId = evalItem.testCaseId === result.testCaseId;
                
                
                if (matchesResponse || matchesIndex || matchesTestCaseId) {
                  analytics.push({
                    ...baseAnalytics,
                    evaluationId: evalResult.nodeId,
                    evaluationName: evalResult.name || evalResult.nodeId,
                    score: typeof evalItem.score === 'number' ? evalItem.score : null,
                    scoreReason: evalItem.reasoning || evalItem.reason || evalItem.explanation || 'N/A',
                    passed: evalItem.passed === true,
                    criteria: evalResult.output?.criteria || evalItem.criteria || 'N/A'
                  });
                  foundEvaluation = true;
                }
              });
            }
          });
          
          // If no evaluation found, add without evaluation data
          if (!foundEvaluation) {
            analytics.push({
              ...baseAnalytics,
              score: null,
              scoreReason: 'No evaluation',
              passed: null,
              criteria: 'N/A'
            });
          }
        });
      }
    });
    
    setAnalyticsData(analytics);
  };

  const startPolling = () => {
    if (polling) return;
    
    setPolling(true);
    const pollInterval = setInterval(async () => {
      try {
        const executionData = await getFlowExecution(flowId, executionId);
        setExecution(executionData);
        
        // Reprocess analytics data when execution completes
        if (executionData.status === 'completed' || executionData.status === 'failed') {
          processAnalyticsData(executionData);
          clearInterval(pollInterval);
          setPolling(false);
        }
      } catch (error) {
        console.error('Error polling execution status:', error);
        clearInterval(pollInterval);
        setPolling(false);
      }
    }, 2000);

    // Store interval reference for cleanup
    return pollInterval;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-celadon-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      case 'running':
        return <Clock className="h-4 w-4 text-gray-500 animate-spin" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-celadon-50 text-celadon-700 border-celadon-200';
      case 'failed':
        return 'bg-gray-50 text-gray-700 border-gray-200';
      case 'running':
        return 'bg-gray-50 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getNodeIcon = (nodeType) => {
    switch (nodeType) {
      case 'promptNode':
        return <MessageSquare className="h-4 w-4 text-celadon-600" />;
      case 'testSetNode':
        return <Database className="h-4 w-4 text-gray-600" />;
      case 'autoEvaluatorNode':
        return <Target className="h-4 w-4 text-gray-600" />;
      case 'splitNode':
        return <GitBranch className="h-4 w-4 text-purple-600" />;
      default:
        return <Zap className="h-4 w-4 text-gray-600" />;
    }
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime) return 'N/A';
    
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const duration = Math.max(0, end - start);
    
    if (duration < 1000) {
      return `${duration}ms`;
    } else if (duration < 60000) {
      return `${(duration / 1000).toFixed(1)}s`;
    } else {
      return `${Math.floor(duration / 60000)}m ${Math.floor((duration % 60000) / 1000)}s`;
    }
  };

  const toggleNodeExpansion = (nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const getExecutionSummary = () => {
    if (!execution?.nodeResults) return {};
    
    const summary = {
      totalNodes: execution.nodeResults.length,
      successful: execution.nodeResults.filter(n => n.status === 'completed').length,
      failed: execution.nodeResults.filter(n => n.status === 'failed').length,
      models: new Set(),
      totalResponses: 0
    };
    
    // Extract models and count responses
    execution.nodeResults.forEach(nodeResult => {
      try {
        const parsedResult = JSON.parse(nodeResult.result);
        if (parsedResult.type === 'promptNode' && parsedResult.output) {
          if (parsedResult.model) summary.models.add(parsedResult.model);
          if (parsedResult.output.results) {
            parsedResult.output.results.forEach(result => {
              if (result.model) summary.models.add(result.model);
            });
            summary.totalResponses += parsedResult.output.results.length;
          }
        }
      } catch (e) {
        // Ignore parsing errors
      }
    });
    
    return summary;
  };

  const filteredAnalyticsData = analyticsData.filter(item => {
    const matchesSearch = searchQuery === '' || 
      item.promptName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.input.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.response.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesScore = selectedScoreFilter === 'all' || 
      (selectedScoreFilter === 'passed' && item.passed === true) ||
      (selectedScoreFilter === 'failed' && item.passed === false) ||
      (selectedScoreFilter === 'no-score' && item.score === null);
    
    const matchesModel = selectedModelFilter === 'all' || item.model === selectedModelFilter;
    
    return matchesSearch && matchesScore && matchesModel;
  });

  const sortedAnalyticsData = [...filteredAnalyticsData].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];
    
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const exportResults = () => {
    const csvContent = [
      // CSV Header
      ['Prompt Name', 'Version', 'Model', 'Input', 'Response', 'Score', 'Passed', 'Score Reason', 'Token Count', 'Execution Time'].join(','),
      // CSV Data
      ...analyticsData.map(item => [
        `"${item.promptName}"`,
        `"${item.promptVersion}"`,
        `"${item.model}"`,
        `"${item.input.replace(/"/g, '""')}"`,
        `"${item.response.replace(/"/g, '""')}"`,
        item.score || 'N/A',
        item.passed === null ? 'N/A' : item.passed,
        `"${item.scoreReason.replace(/"/g, '""')}"`,
        item.tokenCount,
        item.executionTime
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flow-results-${executionId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const getUniqueModels = () => {
    return [...new Set(analyticsData.map(item => item.model))];
  };

  const renderAnalyticsTable = () => {
    if (sortedAnalyticsData.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Found</h3>
          <p className="text-gray-600 mb-4">
            {searchQuery || selectedScoreFilter !== 'all' || selectedModelFilter !== 'all' 
              ? 'Try adjusting your filters or search query.'
              : 'No execution results available yet.'}
          </p>
          {(searchQuery || selectedScoreFilter !== 'all' || selectedModelFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedScoreFilter('all');
                setSelectedModelFilter('all');
              }}
              className="btn btn-outline"
            >
              Clear Filters
            </button>
          )}
        </div>
      );
    }

    if (viewMode === 'cards') {
      return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {sortedAnalyticsData.map((item) => (
            <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4 text-celadon-600" />
                  <span className="font-medium text-gray-900">{item.promptName}</span>
                  <span className="text-xs text-gray-500">v{item.promptVersion}</span>
                </div>
                <div className="flex items-center space-x-2">
                  {item.score !== null && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.passed ? 'bg-celadon-100 text-celadon-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {Math.round(item.score * 100)}%
                    </span>
                  )}
                  <button
                    onClick={() => copyToClipboard(item.response)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Model:</span>
                  <span className="ml-2 text-gray-600">{item.model}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Input:</span>
                  <p className="text-gray-600 mt-1 text-xs bg-gray-50 p-2 rounded">
                    {item.input.length > 100 ? `${item.input.substring(0, 100)}...` : item.input}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Response:</span>
                  <p className="text-gray-600 mt-1 text-xs bg-gray-50 p-2 rounded">
                    {item.response.length > 150 ? `${item.response.substring(0, 150)}...` : item.response}
                  </p>
                </div>
                {item.score !== null && (
                  <div>
                    <span className="font-medium text-gray-700">Score Reason:</span>
                    <p className="text-gray-600 mt-1 text-xs">{item.scoreReason}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-medium text-gray-900 cursor-pointer hover:bg-gray-50" 
                  onClick={() => handleSort('promptName')}>
                <div className="flex items-center space-x-1">
                  <span>Prompt Name</span>
                  {sortConfig.key === 'promptName' && (
                    sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </th>
              <th className="text-left py-3 px-4 font-medium text-gray-900 cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('model')}>
                <div className="flex items-center space-x-1">
                  <span>Model</span>
                  {sortConfig.key === 'model' && (
                    sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </th>
              <th className="text-left py-3 px-4 font-medium text-gray-900">Input</th>
              <th className="text-left py-3 px-4 font-medium text-gray-900">Response</th>
              <th className="text-left py-3 px-4 font-medium text-gray-900 cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('score')}>
                <div className="flex items-center space-x-1">
                  <span>Score</span>
                  {sortConfig.key === 'score' && (
                    sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </th>
              <th className="text-left py-3 px-4 font-medium text-gray-900">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedAnalyticsData.map((item) => (
              <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4">
                  <div className="flex items-center space-x-2">
                    <MessageSquare className="h-4 w-4 text-celadon-600" />
                    <div>
                      <div className="font-medium text-gray-900">{item.promptName}</div>
                      <div className="text-xs text-gray-500">v{item.promptVersion}</div>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center space-x-1">
                    <Cpu className="h-3 w-3 text-gray-400" />
                    <span className="text-sm text-gray-700">{item.model}</span>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="max-w-xs">
                    <p className="text-sm text-gray-700 truncate" title={item.input}>
                      {item.input.length > 50 ? `${item.input.substring(0, 50)}...` : item.input}
                    </p>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="max-w-xs">
                    <p className="text-sm text-gray-700 truncate" title={item.response}>
                      {item.response.length > 60 ? `${item.response.substring(0, 60)}...` : item.response}
                    </p>
                  </div>
                </td>
                <td className="py-3 px-4">
                  {item.score !== null ? (
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        item.passed ? 'bg-celadon-100 text-celadon-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {Math.round(item.score * 100)}%
                      </span>
                      {item.passed ? (
                        <CheckCircle className="h-4 w-4 text-celadon-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-gray-500" />
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-500">No score</span>
                  )}
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => copyToClipboard(item.response)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                      title="Copy response"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => copyToClipboard(item.input)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                      title="Copy input"
                    >
                      <MessageCircle className="h-3 w-3" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner h-12 w-12 mx-auto mb-4"></div>
          <p className="text-lg text-gray-900 font-medium">Loading flow results...</p>
          <p className="text-sm text-gray-600 mt-1">Please wait while we fetch the execution data</p>
        </div>
      </div>
    );
  }

  const summary = getExecutionSummary();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate(`/projects/${projectId}/composer/${flowId}`)}
                  className="btn btn-ghost !p-2"
                  disabled={execution?.status === 'running' || execution?.status === 'pending'}
                  title={execution?.status === 'running' || execution?.status === 'pending' ? 'Please wait for execution to complete' : 'Back to Composer'}
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-gradient-to-br from-celadon-50 to-celadon-100 rounded-2xl shadow-soft">
                    <TrendingUp className="h-8 w-8 text-celadon-700" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">Flow Results</h1>
                    <p className="text-lg text-gray-600 mt-1">
                      {flow?.name} • {project?.name}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={exportResults}
                  className="btn btn-outline"
                  disabled={analyticsData.length === 0}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </button>
                
                {execution && (
                  <div className={`px-4 py-2 rounded-xl text-sm font-medium border shadow-soft ${getStatusColor(execution.status)}`}>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(execution.status)}
                      <span className="capitalize">{execution.status === 'completed' ? 'Complete' : execution.status}</span>
                      {polling && <Activity className="h-4 w-4 animate-pulse" />}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {execution && (
              <div className="mt-6 flex items-center space-x-6 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4" />
                  <span>Started: {new Date(execution.started_at).toLocaleString()}</span>
                </div>
                {execution.completed_at && (
                  <div className="flex items-center space-x-2">
                    <Timer className="h-4 w-4" />
                    <span>Duration: {formatDuration(execution.started_at, execution.completed_at)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Analytics Panel - Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Analytics Header */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-celadon-100 rounded-xl">
                      <BarChart3 className="h-5 w-5 text-celadon-700" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Analytics & Results</h2>
                      <p className="text-sm text-gray-600">
                        Detailed analysis of prompt executions and evaluations
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setViewMode('table')}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                          viewMode === 'table' 
                            ? 'bg-white text-gray-900 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <List className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setViewMode('cards')}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                          viewMode === 'cards' 
                            ? 'bg-white text-gray-900 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <Grid className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Filters */}
              <div className="card-body border-t border-gray-100">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search prompts, inputs, or responses..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-celadon-500 focus:border-celadon-500"
                      />
                    </div>
                  </div>
                  
                  <select
                    value={selectedScoreFilter}
                    onChange={(e) => setSelectedScoreFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-celadon-500 focus:border-celadon-500"
                  >
                    <option value="all">All Scores</option>
                    <option value="passed">Passed</option>
                    <option value="failed">Failed</option>
                    <option value="no-score">No Score</option>
                  </select>
                  
                  <select
                    value={selectedModelFilter}
                    onChange={(e) => setSelectedModelFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-celadon-500 focus:border-celadon-500"
                  >
                    <option value="all">All Models</option>
                    {getUniqueModels().map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                  
                  <button
                    onClick={() => processAnalyticsData(execution)}
                    className="btn btn-ghost"
                    title="Refresh data"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
                
                <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                  <span>
                    Showing {filteredAnalyticsData.length} of {analyticsData.length} results
                  </span>
                  {(searchQuery || selectedScoreFilter !== 'all' || selectedModelFilter !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedScoreFilter('all');
                        setSelectedModelFilter('all');
                      }}
                      className="text-celadon-600 hover:text-celadon-700 font-medium"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Analytics Table/Cards */}
            <div className="card">
              <div className="card-body">
                {renderAnalyticsTable()}
              </div>
            </div>
          </div>

          {/* Enhanced Summary Sidebar */}
          <div className="space-y-6">
            {/* Execution Summary */}
            <div className="card">
              <div className="card-header">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gray-100 rounded-xl">
                    <BarChart3 className="h-5 w-5 text-gray-700" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Execution Summary</h3>
                    <p className="text-sm text-gray-600">Key metrics overview</p>
                  </div>
                </div>
              </div>
              
              <div className="card-body">
                {execution ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">Total Nodes:</span>
                      <span className="text-lg font-bold text-gray-900">{summary.totalNodes}</span>
                    </div>
                    
                    <div className="flex items-center justify-between p-2 bg-celadon-50 rounded-lg">
                      <span className="text-sm font-medium text-celadon-700">Successful:</span>
                      <span className="text-lg font-bold text-celadon-700">{summary.successful}</span>
                    </div>
                    
                    <div className="flex items-center justify-between p-2 bg-gray-100 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">Failed:</span>
                      <span className="text-lg font-bold text-gray-700">{summary.failed}</span>
                    </div>
                    
                    <div className="flex items-center justify-between p-2 bg-celadon-50 rounded-lg">
                      <span className="text-sm font-medium text-celadon-700">Total Responses:</span>
                      <span className="text-lg font-bold text-celadon-700">{summary.totalResponses}</span>
                    </div>
                    
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Models Used:</span>
                        <span className="text-lg font-bold text-gray-700">{summary.models.size}</span>
                      </div>
                      <div className="space-y-1">
                        {Array.from(summary.models).map(model => (
                          <div key={model} className="flex items-center space-x-2 text-xs">
                            <Cpu className="h-3 w-3 text-gray-600" />
                            <span className="text-gray-700">{model}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">Duration:</span>
                      <span className="text-lg font-bold text-gray-700">
                        {formatDuration(execution.started_at, execution.completed_at)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <BarChart3 className="h-6 w-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No execution data available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Score Distribution */}
            {analyticsData.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-celadon-100 rounded-xl">
                      <Target className="h-5 w-5 text-celadon-700" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Score Overview</h3>
                      <p className="text-sm text-gray-600">Evaluation results</p>
                    </div>
                  </div>
                </div>
                
                <div className="card-body">
                  <div className="space-y-3">
                    {(() => {
                      const withScores = analyticsData.filter(item => item.score !== null);
                      const passed = withScores.filter(item => item.passed === true).length;
                      const failed = withScores.filter(item => item.passed === false).length;
                      const avgScore = withScores.length > 0 
                        ? withScores.reduce((sum, item) => sum + item.score, 0) / withScores.length 
                        : 0;
                      
                      return (
                        <>
                          <div className="flex items-center justify-between p-2 bg-celadon-50 rounded-lg">
                            <span className="text-sm font-medium text-celadon-700">Passed:</span>
                            <span className="text-lg font-bold text-celadon-700">{passed}</span>
                          </div>
                          
                          <div className="flex items-center justify-between p-2 bg-gray-100 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Failed:</span>
                            <span className="text-lg font-bold text-gray-700">{failed}</span>
                          </div>
                          
                          <div className="flex items-center justify-between p-2 bg-celadon-50 rounded-lg">
                            <span className="text-sm font-medium text-celadon-700">Avg Score:</span>
                            <span className="text-lg font-bold text-celadon-700">
                              {Math.round(avgScore * 100)}%
                            </span>
                          </div>
                          
                          <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-700">Evaluated:</span>
                            <span className="text-lg font-bold text-gray-700">{withScores.length}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlowResults; 